import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { IconArrowLeft, IconRefresh } from '../../components/icons';
import { Empty, Field, Modal, Skeleton, useAsync, useToast } from '../../components/ui';
import { clearBackup, readBackup, SaveStatus, useAutosave } from '../../journeys/editor/autosave';
import { completeLayout, Layout } from '../../journeys/editor/geometry';
import { sameGraph, sameLayout, stripLayout, useEditor } from '../../journeys/editor/useEditor';
import { TRIGGER_NODE } from '../../journeys/JourneyCanvas';
import { APPROVAL_STATUS, EditorDraft, outcomeLabel, Graph, JourneyDetail, JourneyStats, shortHash, stepOfPath, ValidationIssue, VersionRow } from '../../journeys/model';
import { api, ApiError } from '../../lib/api';
import { ago } from '../../lib/format';
import { currentLocale, t } from '../../lib/i18n';
import { canWrite, useSession } from '../../lib/session';
import { errorText, isAdmin } from '../Contacts';
import { JourneyApproval } from './JourneyApproval';
import { JourneyDiff } from './JourneyDiff';
import { JourneyReport } from './JourneyReport';
import { JourneyRunLog } from './JourneyRunLog';
import { JourneySimulate } from './JourneySimulate';
import { JourneyStepPanel, usePanelData } from './JourneyStepPanel';
import { JourneyStatusPill } from './Journeys';
import '../../styles/journeys.css';

// The editor canvas loads on first use: the main bundle does not carry it (docs/pilot/SPEC.md §12.5).
const EditorCanvas = lazy(() => import('../../journeys/editor/EditorCanvas').then((m) => ({ default: m.EditorCanvas })));

type Tab = 'canvas' | 'approval' | 'simulate' | 'report' | 'enrollments' | 'versions' | 'json';
const TABS: { id: Tab; label: string }[] = [
  { id: 'canvas', label: 'Холст' },
  { id: 'approval', label: 'Согласование' },
  { id: 'simulate', label: 'Симуляция' },
  { id: 'report', label: 'Отчёт' },
  { id: 'enrollments', label: 'Участия' },
  { id: 'versions', label: 'Версии' },
  { id: 'json', label: 'JSON' },
];

/** The header names the active version by what it is doing now, not «работает» for a stopped journey. */
function versionText(j: { status: string; active_version: number | null }): string {
  if (!j.active_version) return t('не опубликован');
  if (j.status === 'stopped') return t('остановлен на v{v}', { v: j.active_version });
  if (j.status === 'paused') return t('на паузе v{v}', { v: j.active_version });
  return t('работает v{v}', { v: j.active_version });
}

export function JourneyPage() {
  const { id = '' } = useParams();
  const { me } = useSession();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const asked = params.get('tab');
  const tab: Tab = TABS.some((x) => x.id === asked) ? (asked as Tab) : 'canvas';
  const setTab = (x: Tab) => setParams(x === 'canvas' ? {} : { tab: x }, { replace: true });
  const writer = canWrite(me) && !me?.support;
  const owner = isAdmin(me?.user.role) && !me?.support;

  const detail = useAsync(() => api.get<JourneyDetail>(`/journeys/${id}`), [id]);
  const versions = useAsync(() => api.get<{ items: VersionRow[] }>(`/journeys/${id}/versions`), [id, detail.data?.latest_version]);
  const j = detail.data;
  const [statsVersion, setStatsVersion] = useState<number | null>(null);
  useEffect(() => setStatsVersion(j ? (j.active_version ?? j.latest_version) || null : null), [j?.active_version, j?.latest_version]); // eslint-disable-line react-hooks/exhaustive-deps
  const stats = useAsync(() => (statsVersion ? api.get<JourneyStats>(`/journeys/${id}/stats?version=${statsVersion}`) : Promise.resolve(null)), [id, statsVersion]);
  // Live statistics: refresh every 30 s while the journey flows (the server caches 30 s too).
  useEffect(() => {
    if (j?.status !== 'active' || tab !== 'canvas') return;
    const tm = setInterval(() => stats.reload(), 30_000);
    return () => clearInterval(tm);
  }, [j?.status, tab]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- the editor (docs/pilot/SPEC.md §12) ----------
  const editor = useEditor();
  const draft = editor.graph;
  const [selected, setSelected] = useState<string | null>(null);
  const [issues, setIssues] = useState<{ errors: ValidationIssue[]; warnings: ValidationIssue[] }>({ errors: [], warnings: [] });
  const [saving, setSaving] = useState(false);
  const baseGraph = useMemo(() => (j?.latest?.graph ? stripLayout(j.latest.graph) : null), [j?.latest?.hash, j?.latest_version, detail.data]); // eslint-disable-line react-hooks/exhaustive-deps
  const baseLayout = (j?.latest?.layout ?? j?.latest?.graph.layout ?? null) as Layout | null;
  /** Positions as saved for the latest version (completed with the automatic ones). */
  const [savedLayout, setSavedLayout] = useState<Layout | null>(null);
  const readOnly = !writer || j?.status === 'stopped';
  const dirty = !!draft && !!baseGraph && !sameGraph(draft, baseGraph);
  const layoutDirty = !!editor.layout && !!savedLayout && !sameLayout(editor.layout, savedLayout);
  const forceReset = useRef(true);

  // A (new) saved version: start from it unless there are unsaved edits of the graph.
  useEffect(() => {
    if (!baseGraph || !j?.latest) return;
    if (!forceReset.current && dirty) return;
    forceReset.current = false;
    editor.reset(baseGraph, baseLayout);
    setSavedLayout(completeLayout(baseGraph, baseLayout));
  }, [baseGraph]); // eslint-disable-line react-hooks/exhaustive-deps

  // An autosaved draft (or a local copy) left from before: offer to continue it.
  const [pending, setPending] = useState<{ source: 'server' | 'browser'; graph: Graph; layout: Layout; rev: number; at: string; by: string | null; base: number } | null>(null);

  const autosave = useAutosave({
    journeyId: id,
    enabled: !readOnly && !!j?.latest,
    graph: draft,
    layout: editor.layout,
    graphDirty: dirty,
    layoutDirty,
    baseVersion: j?.latest_version ?? 0,
    onLayoutSaved: (l) => setSavedLayout(l),
  });

  useEffect(() => {
    if (!j?.latest || readOnly) return;
    let gone = false;
    const latestGraph = stripLayout(j.latest.graph);
    const fromBrowser = () => {
      const b = readBackup(id);
      if (!gone && b && !sameGraph(b.graph, latestGraph)) setPending({ source: 'browser', graph: b.graph, layout: b.layout, rev: 0, at: b.saved_at, by: null, base: b.base_version });
    };
    if (!j.editor_draft) {
      fromBrowser();
      return () => {
        gone = true;
      };
    }
    api
      .get<EditorDraft>(`/journeys/${id}/editor-draft`)
      .then((d) => {
        if (gone) return;
        if (sameGraph(d.graph, latestGraph)) {
          // Same as the saved version (e.g. undone): nothing to continue; adopt its rev so the next save is clean.
          autosave.adopt(d.rev);
          return;
        }
        setPending({ source: 'server', graph: d.graph, layout: d.layout, rev: d.rev, at: d.updated_at, by: d.updated_by.name, base: d.base_version });
      })
      .catch(fromBrowser);
    return () => {
      gone = true;
    };
  }, [id, !!j?.latest, readOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  function continuePending() {
    if (!pending) return;
    editor.load(pending.graph, pending.layout);
    autosave.adopt(pending.rev);
    setPending(null);
    toast(pending.source === 'server' ? t('Черновик загружен — правки сохраняются автоматически') : t('Копия из браузера загружена'));
  }
  async function dropPending() {
    if (!pending) return;
    try {
      if (pending.source === 'server') await api.del(`/journeys/${id}/editor-draft`);
      clearBackup(id);
      autosave.adopt(0);
      setPending(null);
    } catch (err) {
      toast(errorText(err), 'bad');
    }
  }

  const patch = useCallback((fn: (g: Graph) => void) => void editor.patchGraph(fn, `panel:${selected ?? ''}`), [editor, selected]);

  // Validation of the draft as it changes (nothing is written).
  const seq = useRef(0);
  useEffect(() => {
    if (!draft) return;
    const n = ++seq.current;
    const tm = setTimeout(() => {
      api
        .post<{ ok: boolean; errors: ValidationIssue[]; warnings: ValidationIssue[] }>('/journeys/validate', { graph: draft })
        .then((r) => n === seq.current && setIssues({ errors: r.errors, warnings: r.warnings }))
        .catch(() => undefined);
    }, dirty ? 400 : 0);
    return () => clearTimeout(tm);
  }, [draft, dirty]);

  const panelData = usePanelData();

  async function save() {
    if (!draft || !j || !editor.layout) return;
    setSaving(true);
    try {
      const rev = await autosave.cancel();
      const r = await api.put<{ version: number; created: boolean; draft_cleared: boolean; warnings: ValidationIssue[] }>(`/journeys/${j.id}/draft`, {
        graph: draft,
        ...(draft.name && draft.name !== j.name ? { name: draft.name } : {}),
        expected_version: j.latest_version,
        layout: editor.layout,
        ...(rev > 0 ? { draft_rev: rev } : {}),
      });
      toast(r.created ? t('Сохранена версия {v}. Чтобы запустить, запросите согласование.', { v: r.version }) : t('Изменений в шагах нет — версия та же, расположение сохранено'));
      clearBackup(j.id);
      autosave.adopt(r.draft_cleared ? 0 : rev);
      forceReset.current = true;
      setSavedLayout(editor.layout);
      detail.reload();
    } catch (err) {
      if (err instanceof ApiError && err.details?.length) setIssues((x) => ({ ...x, errors: err.details!.map((d) => ({ path: d.path, message: d.message, hint: d.hint, did_you_mean: d.did_you_mean })) }));
      toast(err instanceof ApiError && err.code === 'JOURNEY_VERSION_CONFLICT' ? t('Кто-то сохранил новую версию — обновите страницу; ваши правки сохранены в черновике') : errorText(err), 'bad');
    } finally {
      setSaving(false);
    }
  }
  async function discard() {
    if (!baseGraph) return;
    if (!confirm(t('Отменить все несохранённые изменения? Общий черновик удалится и у других участников команды.'))) return;
    const rev = await autosave.cancel();
    if (rev > 0) await api.del(`/journeys/${id}/editor-draft`).catch(() => undefined);
    clearBackup(id);
    autosave.adopt(0);
    editor.reset(baseGraph, baseLayout);
    setSavedLayout(completeLayout(baseGraph, baseLayout));
    setSelected(null);
  }

  // Another tab or person changed the shared draft.
  async function loadTheirs() {
    try {
      const d = await api.get<EditorDraft>(`/journeys/${id}/editor-draft`);
      editor.load(d.graph, d.layout);
      autosave.adopt(d.rev);
      toast(t('Загружена их версия черновика'));
    } catch (err) {
      toast(errorText(err), 'bad');
    }
  }

  /** Deleting a step that people are on (or passed) in the active version asks first. */
  const confirmDelete = (step: string) => {
    const st = stats.data?.steps[step];
    if (j?.active_version && st && (st.now > 0 || st.entered > 0)) {
      return confirm(t('У шага «{step}» есть статистика активной версии v{v} (сейчас {now}, прошло {entered}). Удалить его из черновика? Активная версия не изменится, пока новая не будет согласована и опубликована.', { step, v: j.active_version, now: st.now, entered: st.entered }));
    }
    return true;
  };

  // ---------- kill switch ----------
  const [stopOpen, setStopOpen] = useState(false);
  const [enrollOpen, setEnrollOpen] = useState(false);
  async function pauseResume() {
    if (!j) return;
    try {
      await api.post(`/journeys/${j.id}/${j.status === 'active' ? 'pause' : 'resume'}`);
      toast(j.status === 'active' ? t('Пауза: шаги ждут, новые участники не входят') : t('Сценарий снова течёт'));
      detail.reload();
    } catch (err) {
      toast(errorText(err), 'bad');
    }
  }

  if (detail.loading && !j) return <div className="stack"><Skeleton h={32} w={320} /><Skeleton h={400} /></div>;
  if (!j) return <Empty title={t('Сценарий не найден')}>{detail.error ? errorText(detail.error) : null}</Empty>;
  const versionList = versions.data?.items ?? [];

  return (
    <>
      <Link to="/journeys" className="btn btn-ghost btn-sm" style={{ marginBottom: 12 }}><IconArrowLeft size={15} /> {t('Все сценарии')}</Link>
      <div className="page-head">
        <div className="grow">
          <h1 style={{ overflowWrap: 'anywhere' }}>{j.name}</h1>
          <p className="row wrap" style={{ gap: 8 }}>
            <JourneyStatusPill j={j} />
            <code className="small">{j.key}</code>
            <span className="small muted">
              {versionText(j)} · {t('последняя v{v}', { v: j.latest_version })}
              {j.latest && <> · <code>{shortHash(j.latest.hash)}</code></>} · {t('согласование:')} {t(APPROVAL_STATUS[j.approval.status]?.label ?? j.approval.status)}
            </span>
          </p>
        </div>
        <div className="row wrap">
          {writer && j.trigger?.type === 'api' && j.status === 'active' && <button className="btn" onClick={() => setEnrollOpen(true)}>{t('Зачислить контакт')}</button>}
          {writer && (j.status === 'active' || (j.status === 'paused' && !j.locked_by_staff)) && <button className="btn" onClick={pauseResume}>{j.status === 'active' ? t('Пауза') : t('Возобновить')}</button>}
          {owner && j.status !== 'stopped' && j.status !== 'draft' && <button className="btn btn-danger" onClick={() => setStopOpen(true)}>{t('Остановить навсегда')}</button>}
        </div>
      </div>

      {j.locked_by_staff && <div className="callout bad" style={{ marginBottom: 12 }}><span><b>{t('Сценарий остановлен MailOcean.')}</b> {j.staff_message}</span></div>}
      {j.status === 'draft' && <div className="callout" style={{ marginBottom: 12 }}>{t('Черновик: ничего не отправляется. Сохраните версию, запросите согласование — после решения владельца или администратора её можно запустить.')}</div>}

      <div className="tabs row" role="tablist">
        {TABS.map((item) => (
          <button key={item.id} role="tab" aria-selected={tab === item.id} className={`tab${tab === item.id ? ' on' : ''}`} onClick={() => setTab(item.id)}>
            {t(item.label)}
            {item.id === 'approval' && j.approval.status === 'pending' ? ' •' : ''}
          </button>
        ))}
      </div>

      {tab === 'canvas' && draft && editor.layout && (
        <div className="stack">
          {pending && (
            <div className="draft-banner" role="status">
              <b>{t('Есть несохранённый черновик')}</b>
              <span className="small muted grow">
                {pending.source === 'server'
                  ? (pending.by ? t('от {time}, {by}', { time: timeOf(pending.at), by: pending.by }) : t('от {time}', { time: timeOf(pending.at) }))
                  : t('в этом браузере, от {time} (не дошёл до сервера)', { time: timeOf(pending.at) })}
                {pending.base !== j.latest_version ? ` · ${t('начат от v{b}, сейчас v{v}', { b: pending.base, v: j.latest_version })}` : ''}
              </span>
              <button className="btn btn-sm" onClick={dropPending}>{t('Отменить изменения')}</button>
              <button className="btn btn-sm btn-primary" onClick={continuePending}>{t('Продолжить')}</button>
            </div>
          )}
          {dirty && (
            <div className="draft-bar" role="status">
              <b>{t('Есть несохранённые изменения')}</b>
              <span className="small muted grow">{t('Черновик сохраняется сам и виден команде; новая версия — по кнопке. Согласование старой версии к ней не относится.')}</span>
              <button className="btn btn-sm" onClick={discard}>{t('Отменить изменения')}</button>
              <button className="btn btn-sm btn-primary" disabled={saving || issues.errors.length > 0} onClick={save} title={issues.errors.length ? t('Сначала исправьте ошибки') : undefined}>{saving ? t('Сохраняю…') : t('Сохранить версию')}</button>
            </div>
          )}
          <div className="journey-grid">
            <div className="stack" style={{ minWidth: 0 }}>
              <Suspense fallback={<Skeleton h={480} />}>
              <EditorCanvas
                editor={editor}
                readOnly={readOnly}
                stats={stats.data}
                issues={issues}
                selected={selected}
                onSelect={(sid) => setSelected(sid)}
                confirmDelete={confirmDelete}
                status={readOnly ? null : <SaveLine status={autosave.status} dirty={dirty} layoutDirty={layoutDirty} />}
                toolbar={
                  <>
                    <label className="small muted">{t('Статистика')}
                      <select className="select input" style={{ marginLeft: 6, width: 'auto', padding: '4px 28px 4px 8px' }} value={statsVersion ?? ''} onChange={(e) => setStatsVersion(Number(e.target.value) || null)}>
                        {versionList.map((v) => <option key={v.version} value={v.version}>v{v.version}</option>)}
                      </select>
                    </label>
                    <button className="btn btn-sm btn-ghost" onClick={() => stats.reload()} title={t('Обновить статистику')} aria-label={t('Обновить статистику')}><IconRefresh size={14} /></button>
                    {stats.data && <span className="small muted">{t('посчитано {ago}', { ago: ago(stats.data.computed_at) })}</span>}
                  </>
                }
              />
              </Suspense>
              {stats.data && <EnrollmentSummary stats={stats.data} />}
            </div>
            <div className="stack" style={{ minWidth: 0 }}>
              {selected && (selected === TRIGGER_NODE || draft.steps[selected]) ? (
                <JourneyStepPanel
                  graph={draft}
                  id={selected}
                  patch={patch}
                  onSelect={setSelected}
                  onClose={() => setSelected(null)}
                  data={panelData}
                  issues={[...issues.errors, ...issues.warnings]}
                  disabled={readOnly}
                  testVersion={dirty ? null : j.latest_version || null}
                  journeyId={j.id}
                />
              ) : (
                <div className="card card-pad stack">
                  <h3 style={{ margin: 0 }}>{t('Шаги')}</h3>
                  <p className="small" style={{ margin: 0 }}>{t('Перетащите шаг из палитры на холст или прямо на связь — он встанет в неё. Связь тянется от кружка-выхода к шагу. Нажмите на шаг — здесь откроются его настройки, на «Старт» — триггер, цель, контрольная группа, тихие часы.')}</p>
                  <p className="small muted" style={{ margin: 0 }}>{t('Без мыши: Tab — по шагам, A — добавить, C — соединить, M — переместить, ? — все клавиши. На телефоне удобнее «Список шагов».')}</p>
                  {readOnly && <p className="small muted" style={{ margin: 0 }}>{writer ? t('Сценарий остановлен — холст только для чтения.') : t('У вас роль только для чтения.')}</p>}
                </div>
              )}
              {(issues.errors.length > 0 || issues.warnings.length > 0) && (
                <div className="card card-pad stack">
                  <h3 style={{ margin: 0 }}>{t('Проверка графа')}</h3>
                  <ul className="issues">
                    {issues.errors.map((e, i) => (
                      <li key={`e${i}`}>
                        <button onClick={() => setSelected(stepOfPath(e.path) ?? TRIGGER_NODE)}><code>{e.path}</code></button> — {e.message}{e.hint ? ` · ${e.hint}` : ''}{e.did_you_mean ? <> · {t('может быть,')} <b>{e.did_you_mean}</b>?</> : null}
                      </li>
                    ))}
                    {issues.warnings.map((w, i) => (
                      <li key={`w${i}`} className="warning">
                        <button onClick={() => setSelected(stepOfPath(w.path) ?? TRIGGER_NODE)}><code>{w.path}</code></button> — {w.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {autosave.status.kind === 'conflict' && (
        <Modal
          title={t('Черновик изменили')}
          onClose={() => undefined}
          footer={
            <>
              <button className="btn" onClick={loadTheirs}>{t('Загрузить их версию')}</button>
              <button className="btn btn-primary" onClick={() => autosave.overwrite((autosave.status as Extract<SaveStatus, { kind: 'conflict' }>).rev)}>{t('Перезаписать моей')}</button>
            </>
          }
        >
          <p>{t('Черновик изменил(а) {by}{at} — в другой вкладке или на другом устройстве.', { by: autosave.status.by ?? t('другой участник'), at: autosave.status.at ? ` ${t('в {time}', { time: timeOf(autosave.status.at) })}` : '' })}</p>
          <p className="small muted">{t('«Загрузить их версию» заменит ваши несохранённые правки (вернуть их можно кнопкой «Отменить»). «Перезаписать моей» сохранит ваш вариант поверх.')}</p>
        </Modal>
      )}

      {tab === 'approval' && <JourneyApproval journey={j} owner={owner} writer={writer} onChanged={() => { detail.reload(); versions.reload(); }} />}
      {tab === 'simulate' && (writer ? <JourneySimulate journeyId={j.id} versions={versionList} defaultVersion={j.active_version ?? j.latest_version} /> : <div className="callout">{t('Симуляция показывает адреса контактов — она доступна ролям, которые могут менять сценарии.')}</div>)}
      {tab === 'report' && <JourneyReport journeyId={j.id} versions={versionList} defaultVersion={j.active_version ?? j.latest_version} onEnableHoldout={writer ? () => { setTab('canvas'); setSelected(TRIGGER_NODE); } : undefined} />}
      {tab === 'enrollments' && <JourneyRunLog journeyId={j.id} graph={j.active?.graph ?? j.latest?.graph ?? null} initial={params.get('e')} />}
      {tab === 'versions' && <JourneyDiff journey={j} versions={versionList} owner={owner} onChanged={() => { detail.reload(); stats.reload(); }} />}
      {tab === 'json' && draft && <JsonTab draft={draft} disabled={readOnly} onApply={(g) => { editor.load(g, (g.layout as Layout | undefined) ?? editor.layout); setTab('canvas'); }} />}

      {stopOpen && <StopModal j={j} onClose={() => setStopOpen(false)} onStopped={() => { setStopOpen(false); detail.reload(); }} />}
      {enrollOpen && <EnrollModal j={j} onClose={() => setEnrollOpen(false)} />}
    </>
  );
}

const timeOf = (iso: string) => new Date(iso).toLocaleTimeString(currentLocale(), { hour: '2-digit', minute: '2-digit' });

/** «Черновик сохранён 12:41» / «Сохраняю…» / «Нет связи — изменения в браузере». */
function SaveLine({ status, dirty, layoutDirty }: { status: SaveStatus; dirty: boolean; layoutDirty: boolean }) {
  switch (status.kind) {
    case 'saving':
      return <>{t('Сохраняю…')}</>;
    case 'pending':
      return <>{dirty ? t('Сохраню через секунду…') : layoutDirty ? t('Сохраню расположение через секунду…') : ''}</>;
    case 'saved':
      return <>{status.what === 'layout' ? t('Расположение сохранено {time}', { time: timeOf(status.at) }) : status.what === 'cleared' ? t('Черновик совпал с версией и удалён') : t('Черновик сохранён {time}', { time: timeOf(status.at) })}</>;
    case 'offline':
      return <span className="warn">{t('Нет связи — изменения в браузере')}</span>;
    case 'error':
      return <span className="warn">{t('Не сохранено: {text}', { text: status.text })}</span>;
    case 'conflict':
      return <span className="warn">{t('Черновик изменили в другом месте')}</span>;
    default:
      return null;
  }
}

function EnrollmentSummary({ stats }: { stats: JourneyStats }) {
  const e = stats.enrollments;
  const sum = (prefix: string) => Object.entries(e).filter(([k]) => k.startsWith(prefix)).reduce((a, [, v]) => a + v, 0);
  const exits = Object.entries(stats.exits);
  const loc = currentLocale();
  return (
    <div className="grid grid-kpi">
      <div className="card kpi"><div className="label">{t('капель в пути')}</div><div className="value">{(e['treatment:active'] ?? 0).toLocaleString(loc)}</div><div className="sub">{t('всего вошло {n}', { n: sum('treatment:').toLocaleString(loc) })}</div></div>
      <div className="card kpi"><div className="label">{t('контрольная группа')}</div><div className="value">{sum('control:').toLocaleString(loc)}</div><div className="sub">{t('писем не получают')}</div></div>
      <div className="card kpi"><div className="label">{t('дошли до конца')}</div><div className="value">{(e['treatment:completed'] ?? 0).toLocaleString(loc)}</div><div className="sub">{t('вышли {n}', { n: (e['treatment:exited'] ?? 0).toLocaleString(loc) })}{exits.length ? ` · ${exits.slice(0, 3).map(([k, v]) => `${t(outcomeLabel(k))} ${v}`).join(', ')}` : ''}</div></div>
    </div>
  );
}

function JsonTab({ draft, disabled, onApply }: { draft: Graph; disabled: boolean; onApply: (g: Graph) => void }) {
  const [text, setText] = useState(JSON.stringify(draft, null, 2));
  const [err, setErr] = useState('');
  return (
    <div className="card card-pad stack">
      <p className="small muted" style={{ margin: 0 }}>{t('Тот же граф, что принимает')} <code>PUT /v1/journeys/:key</code>. {t('Применённые правки попадут в черновик; новая версия — по кнопке «Сохранить версию».')}</p>
      <textarea className="input input-mono" rows={24} value={text} spellCheck={false} onChange={(e) => setText(e.target.value)} disabled={disabled} aria-label={t('Граф сценария в JSON')} />
      {err && <div className="callout bad">{err}</div>}
      {!disabled && (
        <button className="btn btn-primary" style={{ alignSelf: 'flex-start' }} onClick={() => {
          try {
            const g = JSON.parse(text) as Graph;
            if (!g || typeof g !== 'object' || !g.steps) throw new Error(t('нет поля steps'));
            onApply(g);
          } catch (e) {
            setErr(t('Не применено: {m}', { m: (e as Error).message }));
          }
        }}>{t('Применить к черновику')}</button>
      )}
    </div>
  );
}

function StopModal({ j, onClose, onStopped }: { j: JourneyDetail; onClose: () => void; onStopped: () => void }) {
  const toast = useToast();
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  async function stop() {
    setBusy(true);
    try {
      const r = await api.post<{ exited: number; cancelled: number }>(`/journeys/${j.id}/stop`, { confirm: typed.trim() });
      toast(t('Остановлено: завершено участий {e}, отменено писем в очереди {c}', { e: r.exited, c: r.cancelled }));
      onStopped();
    } catch (err) {
      toast(errorText(err), 'bad');
      setBusy(false);
    }
  }
  return (
    <Modal title={t('Остановить сценарий навсегда')} onClose={onClose} footer={<><button className="btn" onClick={onClose}>{t('Отмена')}</button><button className="btn btn-danger" disabled={busy || typed.trim() !== j.key} onClick={stop}>{t('Остановить навсегда')}</button></>}>
      <p>{t('Все участия завершатся, ожидания и таймеры удалятся, письма сценария в очереди будут отменены. Вернуть нельзя — только создать новый сценарий.')}</p>
      <p className="small muted">{t('Письма, уже переданные провайдеру, не отзываются. Для временной остановки есть «Пауза».')}</p>
      <Field label={t('Введите ключ сценария: {key}', { key: j.key })}>
        <input className="input input-mono" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={j.key} autoFocus />
      </Field>
    </Modal>
  );
}

function EnrollModal({ j, onClose }: { j: JourneyDetail; onClose: () => void }) {
  const toast = useToast();
  const [contact, setContact] = useState('');
  const [ctx, setCtx] = useState('');
  async function enroll() {
    try {
      const ref = contact.trim().startsWith('con_') ? { id: contact.trim() } : contact.includes('@') ? { email: contact.trim() } : { external_id: contact.trim() };
      const r = await api.post<{ status: string; reason?: string }>(`/journeys/${j.id}/enrollments`, { contact: ref, ...(ctx.trim() ? { context: JSON.parse(ctx) } : {}) });
      toast(r.status === 'enrolled' ? t('Зачислен') : r.status === 'already_enrolled' ? t('Уже в сценарии') : r.reason ? t('Не подходит: {r}', { r: r.reason }) : t('Не подходит'), r.status === 'not_eligible' ? 'bad' : 'ok');
      onClose();
    } catch (err) {
      toast(err instanceof SyntaxError ? t('Контекст: JSON не разбирается') : errorText(err), 'bad');
    }
  }
  return (
    <Modal title={t('Зачислить контакт')} onClose={onClose} footer={<><button className="btn" onClick={onClose}>{t('Отмена')}</button><button className="btn btn-primary" disabled={!contact.trim()} onClick={enroll}>{t('Зачислить')}</button></>}>
      <Field label={t('Контакт')} hint={t('email, external_id или con_…')}><input className="input" value={contact} onChange={(e) => setContact(e.target.value)} autoFocus /></Field>
      <Field label={t('Контекст (JSON, необязательно)')}><textarea className="input input-mono" rows={3} value={ctx} onChange={(e) => setCtx(e.target.value)} /></Field>
      <p className="small muted">{t('Ручное зачисление записывается в журнал действий. Из кода —')} <code>POST /v1/journeys/{j.key}/enrollments</code>.</p>
    </Modal>
  );
}
